// Offline Capture Store — public API.
//
// Browser-side IndexedDB-backed store that powers offline-first live editing
// of CdA sessions. Owns three responsibilities:
//
//   1. Session state cache (`sessions` store) — fast lookup of last-known
//      state per sessionId so an unmounted Stimulus controller can rehydrate
//      without a network round-trip.
//   2. Write-ahead log (`ops_log` store) — every mutation is appended in
//      order so the sync engine (Slice 11) can replay against the server.
//   3. Periodic snapshots (`snapshots` store) — every 5 seconds while a
//      session is dirty we freeze full state, providing crash recovery and
//      undo points without bloating the WAL.
//
// All methods are static + Promise-returning. The store keeps a single cached
// IDBDatabase connection per database name (see `./db`).

import {
  STORE_OPS_LOG,
  STORE_SESSIONS,
  STORE_SNAPSHOTS,
  awaitRequest,
  awaitTransaction,
  openDatabase,
} from "./db";
import type { Op, OpStatus, SessionState, Snapshot, UsageEstimate } from "./types";

const DEFAULT_LIST_SNAPSHOTS_LIMIT = 10;
const DEFAULT_RETENTION_DAYS = 7;
const MAX_SNAPSHOTS_PER_SESSION = 100;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Threshold (0..1) at which `estimateUsage()` emits a quota warning event.
 * Tuned to 0.8 to give the UI ~20% headroom for triggering cleanup or
 * surfacing a recovery prompt before IndexedDB starts rejecting writes.
 */
export const QUOTA_WARNING_THRESHOLD = 0.8;

/**
 * Custom event name dispatched on `OfflineStore.events` when storage usage
 * crosses the warning threshold. Subscribe with
 * `OfflineStore.events.addEventListener(QUOTA_WARNING_EVENT, fn)`.
 *
 * We use a dedicated EventTarget rather than `window` so the store stays
 * usable in non-DOM contexts (server-side rendering, web workers, tests).
 */
export const QUOTA_WARNING_EVENT = "offline-capture-store:quota-warning";

/**
 * Default cadence (ms) for `startAutoSnapshot()`. PRD calls for 5s.
 */
export const DEFAULT_AUTO_SNAPSHOT_INTERVAL_MS = 5000;

/**
 * Cheap, stable string hash used to detect dirty state between auto-snapshot
 * ticks. Not cryptographic — just a 32-bit integer derived from the
 * stringified state. Collisions are unlikely for typical session payloads
 * and harmless (worst case: we skip a snapshot we should have taken).
 */
function hashState(state: SessionState): string {
  const json = JSON.stringify(state);
  let hash = 0;
  for (let i = 0; i < json.length; i += 1) {
    hash = ((hash << 5) - hash + json.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
}

interface AutoSnapshotEntry {
  intervalId: ReturnType<typeof setInterval>;
  lastHash: string | null;
}

/**
 * Per-session auto-snapshot timers. Keyed by sessionId so multiple sessions
 * can run their own ticking intervals in parallel without interference.
 */
const autoSnapshotTimers = new Map<string, AutoSnapshotEntry>();

export const OFFLINE_CAPTURE_STORE_VERSION = "0.1.0";

interface SessionRecord {
  sessionId: string;
  state: SessionState;
}

export class OfflineStore {
  /**
   * Pub/sub channel for store-level notifications (e.g.
   * `QUOTA_WARNING_EVENT`). Use `addEventListener` / `removeEventListener`.
   * Lives at class level so all sessions share one bus — wire-once UIs
   * receive every warning regardless of which call triggered it.
   */
  static readonly events: EventTarget = new EventTarget();
  /**
   * Fetch the last-saved state for `sessionId`. Returns `null` when the
   * session has never been persisted (so callers can branch on
   * "rehydrate vs. fresh load").
   */
  static async getSession(sessionId: string): Promise<SessionState | null> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_SESSIONS, "readonly");
    const store = tx.objectStore(STORE_SESSIONS);
    const record = await awaitRequest(store.get(sessionId) as IDBRequest<SessionRecord | undefined>);
    return record ? record.state : null;
  }

  /**
   * Persist (or overwrite) the full state blob for `sessionId`. The store
   * does not diff or merge — callers send the complete next-state snapshot.
   */
  static async saveSession(sessionId: string, state: SessionState): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_SESSIONS, "readwrite");
    tx.objectStore(STORE_SESSIONS).put({ sessionId, state });
    await awaitTransaction(tx);
  }

  /**
   * Append a single op to the WAL for `sessionId`. The `id` field on the
   * input is ignored — IndexedDB assigns one via autoIncrement.
   *
   * `sessionId` on the op record is normalised to the explicit argument so
   * callers can not accidentally store mismatched session ids.
   */
  static async appendOp(sessionId: string, op: Op): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_OPS_LOG, "readwrite");
    const { id: _id, ...rest } = op;
    tx.objectStore(STORE_OPS_LOG).add({ ...rest, sessionId });
    await awaitTransaction(tx);
  }

  /**
   * List every op recorded for `sessionId` in insertion order (autoIncrement
   * id ascending). Returns an empty array when nothing has been appended.
   */
  static async listOps(sessionId: string): Promise<Op[]> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_OPS_LOG, "readonly");
    const index = tx.objectStore(STORE_OPS_LOG).index("sessionId");
    const ops = await awaitRequest(index.getAll(IDBKeyRange.only(sessionId)) as IDBRequest<Op[]>);
    // getAll on a non-unique index returns matches in storage (id) order,
    // which equals insertion order because id is autoIncrement.
    return ops;
  }

  /**
   * Transition the persisted `status` of an op identified by `clientOpId`.
   * Used to mark ops as `syncing` / `synced` / `failed` / `discarded` as the
   * capture journal drains them and the recovery panel retires them. No-op if
   * the clientOpId is unknown — keeps the call site simple for retries that
   * race a deletion.
   */
  static async updateOpStatus(clientOpId: string, status: OpStatus): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_OPS_LOG, "readwrite");
    const opsStore = tx.objectStore(STORE_OPS_LOG);
    const index = opsStore.index("clientOpId");
    const existing = await awaitRequest(index.get(IDBKeyRange.only(clientOpId)) as IDBRequest<Op | undefined>);
    if (existing !== undefined) {
      opsStore.put({ ...existing, status });
    }
    await awaitTransaction(tx);
  }

  /**
   * Replace the persisted `payload` of an op identified by `clientOpId`.
   * Used by the capture journal (#823) when a server-spawned run replaces the
   * local "new" placeholder id — every pending op for that row is re-pointed
   * at the real run id so retries do not spawn duplicate runs. No-op when the
   * clientOpId is unknown, mirroring `updateOpStatus`.
   */
  static async updateOpPayload(clientOpId: string, payload: unknown): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_OPS_LOG, "readwrite");
    const opsStore = tx.objectStore(STORE_OPS_LOG);
    const index = opsStore.index("clientOpId");
    const existing = await awaitRequest(index.get(IDBKeyRange.only(clientOpId)) as IDBRequest<Op | undefined>);
    if (existing !== undefined) {
      opsStore.put({ ...existing, payload });
    }
    await awaitTransaction(tx);
  }

  /**
   * Snapshot the current saveSession state of `sessionId`. Reads the latest
   * persisted state inside the same transaction so the snapshot is consistent
   * with what `getSession` would return at the same instant.
   *
   * No-ops silently when the session has never been saved — there is nothing
   * to snapshot, and we prefer not to throw because the auto-snapshot loop
   * may race a freshly-mounted session before the first save.
   */
  static async takeSnapshot(sessionId: string): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction([STORE_SESSIONS, STORE_SNAPSHOTS], "readwrite");
    const sessionRecord = await awaitRequest(
      tx.objectStore(STORE_SESSIONS).get(sessionId) as IDBRequest<{ sessionId: string; state: SessionState } | undefined>,
    );
    if (sessionRecord !== undefined) {
      tx.objectStore(STORE_SNAPSHOTS).add({
        sessionId,
        state: sessionRecord.state,
        takenAt: Date.now(),
      });
    }
    await awaitTransaction(tx);
  }

  /**
   * Return up to `limit` most-recent snapshots for `sessionId`, ordered
   * newest-first by `takenAt`. Default limit is 10 — keeps recovery UIs
   * snappy without paginating.
   */
  static async listSnapshots(sessionId: string, limit: number = DEFAULT_LIST_SNAPSHOTS_LIMIT): Promise<Snapshot[]> {
    const db = await openDatabase();
    const tx = db.transaction(STORE_SNAPSHOTS, "readonly");
    const index = tx.objectStore(STORE_SNAPSHOTS).index("sessionId");
    const all = await awaitRequest(index.getAll(IDBKeyRange.only(sessionId)) as IDBRequest<Snapshot[]>);
    return all.sort((a, b) => b.takenAt - a.takenAt).slice(0, limit);
  }

  /**
   * Restore session state from a previously-taken snapshot, overwriting the
   * current `sessions` row. The snapshot row itself is NOT deleted — keeping
   * it around makes "undo the restore" trivial (just restore the older one)
   * and matches the expected recovery-panel UX (snapshots are checkpoints,
   * not consumables).
   *
   * Throws when:
   *  - no snapshot with the given `snapshotId` exists, or
   *  - the snapshot belongs to a different session (defensive against UI bugs
   *    that pass the wrong id from a multi-session list).
   */
  static async restoreFromSnapshot(sessionId: string, snapshotId: number): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction([STORE_SESSIONS, STORE_SNAPSHOTS], "readwrite");

    const snapshot = await awaitRequest(
      tx.objectStore(STORE_SNAPSHOTS).get(snapshotId) as IDBRequest<Snapshot | undefined>,
    );
    if (snapshot === undefined) {
      throw new Error(`Snapshot ${snapshotId} not found`);
    }
    if (snapshot.sessionId !== sessionId) {
      throw new Error(
        `Snapshot ${snapshotId} belongs to session ${snapshot.sessionId}, not ${sessionId}`,
      );
    }

    tx.objectStore(STORE_SESSIONS).put({ sessionId, state: snapshot.state });
    await awaitTransaction(tx);
  }

  /**
   * Purge snapshots subject to two independent rules, applied per-session:
   *
   *  1. **Retention** — drop anything older than `retentionDays` (default 7).
   *  2. **LRU cap**   — keep at most `MAX_SNAPSHOTS_PER_SESSION` (100) per
   *     session, dropping oldest first.
   *
   * Run this opportunistically (e.g. on app boot or after long idle) — it
   * touches every snapshot so it is not free, but it is bounded by IndexedDB
   * I/O and runs in a single readwrite transaction.
   */
  static async cleanup(retentionDays: number = DEFAULT_RETENTION_DAYS): Promise<void> {
    const cutoff = Date.now() - retentionDays * MS_PER_DAY;
    const db = await openDatabase();
    const tx = db.transaction(STORE_SNAPSHOTS, "readwrite");
    const store = tx.objectStore(STORE_SNAPSHOTS);
    const all = await awaitRequest(store.getAll() as IDBRequest<Snapshot[]>);

    // Group by session so the LRU cap is per-session, not global.
    const bySession = new Map<string, Snapshot[]>();
    for (const snap of all) {
      const list = bySession.get(snap.sessionId) ?? [];
      list.push(snap);
      bySession.set(snap.sessionId, list);
    }

    for (const [, snaps] of bySession) {
      // Newest first
      snaps.sort((a, b) => b.takenAt - a.takenAt);

      // Mark for deletion: anything past the LRU cap.
      const overCap = snaps.slice(MAX_SNAPSHOTS_PER_SESSION);
      // Mark for deletion: anything older than the cutoff (within the cap).
      const expired = snaps
        .slice(0, MAX_SNAPSHOTS_PER_SESSION)
        .filter((s) => s.takenAt < cutoff);

      for (const snap of [...overCap, ...expired]) {
        if (snap.id !== undefined) store.delete(snap.id);
      }
    }

    await awaitTransaction(tx);
  }

  /**
   * Probe `navigator.storage.estimate()` and return normalised quota / usage
   * numbers plus a 0..1 percentage. When the API is unavailable (e.g. SSR or
   * old browser) we return zeros so callers can branch safely without
   * try/catching here.
   *
   * Side effect: dispatches `QUOTA_WARNING_EVENT` on `OfflineStore.events`
   * whenever usage crosses `QUOTA_WARNING_THRESHOLD`. This is the documented
   * escape hatch for the recovery-panel UI to nudge the user toward
   * `cleanup()`.
   */
  static async estimateUsage(): Promise<UsageEstimate> {
    const estimateFn = globalThis.navigator?.storage?.estimate?.bind(
      globalThis.navigator.storage,
    );
    if (!estimateFn) {
      return { quota: 0, usage: 0, percentage: 0 };
    }

    const { quota = 0, usage = 0 } = await estimateFn();
    const percentage = quota > 0 ? usage / quota : 0;
    const result: UsageEstimate = { quota, usage, percentage };

    if (percentage > QUOTA_WARNING_THRESHOLD) {
      const event = new CustomEvent(QUOTA_WARNING_EVENT, { detail: result });
      OfflineStore.events.dispatchEvent(event);
    }

    return result;
  }

  /**
   * Start a per-session interval that snapshots `sessionId` whenever the
   * persisted state has changed since the previous tick.
   *
   * Dirty detection compares a cheap 32-bit hash of the current `getSession`
   * state against the hash captured at the last tick (or at startup, if no
   * tick has fired yet). If the session has never been saved, the baseline
   * hash starts as `null` and the first non-null state will be considered
   * dirty.
   *
   * Calling this for a sessionId that already has a running interval is a
   * no-op — we do not stack timers. Use `stopAutoSnapshot` first if you
   * need to change the cadence.
   */
  static startAutoSnapshot(sessionId: string, intervalMs: number = DEFAULT_AUTO_SNAPSHOT_INTERVAL_MS): void {
    if (autoSnapshotTimers.has(sessionId)) return;

    const entry: AutoSnapshotEntry = {
      intervalId: setInterval(() => {
        void OfflineStore.runAutoSnapshotTick(sessionId);
      }, intervalMs),
      lastHash: null,
    };

    // Seed the baseline asynchronously so that *changes after start* are what
    // counts as dirty — without this, the very first tick would always
    // snapshot whatever happened to be in the store at startup.
    void OfflineStore.getSession(sessionId).then((state) => {
      // Only seed if startAutoSnapshot is still active and no tick has run yet.
      const current = autoSnapshotTimers.get(sessionId);
      if (current && current.lastHash === null) {
        current.lastHash = state === null ? null : hashState(state);
      }
    });

    autoSnapshotTimers.set(sessionId, entry);
  }

  /**
   * Stop the auto-snapshot interval for `sessionId`. No-op if there is no
   * running interval — safe to call defensively from teardown code.
   */
  static stopAutoSnapshot(sessionId: string): void {
    const entry = autoSnapshotTimers.get(sessionId);
    if (!entry) return;
    clearInterval(entry.intervalId);
    autoSnapshotTimers.delete(sessionId);
  }

  /**
   * Internal tick handler. Public only so tests / instrumentation can
   * trigger a tick deterministically without waiting for the timer.
   */
  static async runAutoSnapshotTick(sessionId: string): Promise<void> {
    const entry = autoSnapshotTimers.get(sessionId);
    if (!entry) return;

    const state = await OfflineStore.getSession(sessionId);
    if (state === null) return;

    const currentHash = hashState(state);
    if (currentHash === entry.lastHash) return;

    await OfflineStore.takeSnapshot(sessionId);
    entry.lastHash = currentHash;
  }
}

export type { Op, OpStatus, OpType, SessionState, Snapshot, UsageEstimate } from "./types";
